import { atom } from 'jotai'

import {
  type RescheduleDispatchDetailRequest,
  type SaveDispatchGroupScheduleRequest
} from '@/features/vehicle-dispatch-boards/api'
import {
  describeTimeRestrictionViolations,
  isAnyDetailTimeOutOfRestriction,
  type TimeRestrictionCheckDetail
} from '@/features/vehicle-dispatch-boards/utils'

/**
 * 矢羽根の移動・リサイズで時間制限外になる場合の警告モーダルの保留ペイロード。
 * 経路ごとに送信先 API（≒mutation hook）が異なるため、種別ごとに保持する。
 */
export type TimeRestrictionConfirmModalPending =
  // 単一矢羽根の移動（onDragEnd 再配車パターン）
  | { kind: 'reschedule'; request: RescheduleDispatchDetailRequest }
  // 束ね親の一括シフト（onDragEnd 束ね親矢羽根ドラッグ）
  | { kind: 'groupShift'; request: SaveDispatchGroupScheduleRequest }
  // 単一矢羽根のリサイズ
  | { kind: 'resize'; request: RescheduleDispatchDetailRequest }
  // 束ね親のリサイズ（右端ドラッグ）
  | { kind: 'groupResize'; request: SaveDispatchGroupScheduleRequest }

export interface TimeRestrictionConfirmModalState {
  isOpen: boolean
  pending: TimeRestrictionConfirmModalPending | null
  /** 制限外になった発着時間の内訳（表示用スナップショット。モーダルを開く時点で確定） */
  reasons: string[]
}

export const timeRestrictionConfirmModalAtom =
  atom<TimeRestrictionConfirmModalState>({
    isOpen: false,
    pending: null,
    reasons: []
  })

timeRestrictionConfirmModalAtom.debugLabel = 'timeRestrictionConfirmModalAtom'

/**
 * 移動・リサイズ確定前の明細群が時間制限外かどうかを判定し、制限外なら警告モーダルに
 * セットする state を返す（制限内なら null = そのまま保存してよい）。
 * 呼び出し側は次の形で使う想定:
 * ```ts
 * const confirmState = buildTimeRestrictionConfirmState(details, { kind: 'resize', request })
 * if (confirmState) { set(timeRestrictionConfirmModalAtom, confirmState); return }
 * mutate(request)
 * ```
 */
export const buildTimeRestrictionConfirmState = (
  details: TimeRestrictionCheckDetail[],
  pending: TimeRestrictionConfirmModalPending
): TimeRestrictionConfirmModalState | null =>
  isAnyDetailTimeOutOfRestriction(details)
    ? {
        isOpen: true,
        pending,
        reasons: describeTimeRestrictionViolations(details)
      }
    : null
